import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { ButtonComponent, MbsSize } from 'mbs-ui-kit';
import { BuildsFacade } from 'mbs-ui-shared@facades/builds.facade';
import { BetaBackupRequest, BetaRequestService } from 'mbs-ui-shared@services/beta-request.service';
import { finalize } from 'rxjs';

@Component({
  selector: 'mbs-beta-feature-request',
  templateUrl: './beta-feature-request.component.html',
  styleUrls: ['./beta-feature-request.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, I18NextPipe]
})
export class BetaFeatureRequestComponent implements OnInit {
  private ability = inject(AbilityService);
  private buildsFacade = inject(BuildsFacade);
  private betaRequestService = inject(BetaRequestService);
  private destroyRef = inject(DestroyRef);

  public readonly buttonSize = MbsSize;

  public readonly allowBetaRequest = signal(this.ability.can('read', 'AllowBetaRequest'));
  public readonly featureName = input.required<string>();
  public readonly buildName = input<string | null | undefined>(null);
  public readonly version = input<number | string | null | undefined>(null);
  public readonly betaAvailable = signal(false);
  public readonly loading = signal(false);
  public readonly isSubAdmin = signal(this.ability.can('read', 'SubAdmin'));
  public readonly requested = signal(false);

  private readonly requestDateLoaded = signal(false);
  private readonly betaAvailabilityLoaded = signal(false);

  public readonly loaded = computed(() => this.requestDateLoaded() && this.betaAvailabilityLoaded());
  public readonly showBaseLayout = computed(() => this.isSubAdmin() && !this.allowBetaRequest());

  ngOnInit(): void {
    this.betaRequestService
      .requestBetaBackupDate()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (requested) => {
          this.requested.set(requested);
          this.requestDateLoaded.set(true);
        },
        error: () => this.requestDateLoaded.set(true)
      });

    this.buildsFacade.buildsSettings$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((settings) => {
      if (settings?.isBetaAvailable === null || settings?.isBetaAvailable === undefined) return;

      this.betaAvailable.set(settings.isBetaAvailable);
      this.betaAvailabilityLoaded.set(true);
    });
  }

  request(): void {
    if (this.loading() || this.requested() || this.betaAvailable()) return;

    const payload = this.createPayload();
    this.loading.set(true);

    this.betaRequestService
      .requestBetaBackup(payload)
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.requested.set(true);
        }
      });
  }

  private createPayload(): BetaBackupRequest {
    const payload: BetaBackupRequest = { featureName: this.featureName(), url: window.location.href };
    const buildName = this.buildName();
    const version = this.getVersion(this.version());

    if (buildName) payload.buildName = buildName;
    if (version !== null) payload.version = version;

    return payload;
  }

  private getVersion(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') return null;

    const version = typeof value === 'string' ? Number(value.split('.').join('').substring(0, 3)) : value;

    return Number.isFinite(version) && version > 0 ? version : null;
  }
}
